<script setup lang="ts">
  import { defineProps, type PropType, computed } from 'vue'
  import { ExclamationCircleIcon } from '@heroicons/vue/24/outline'
  import { type RuleExpression, useField } from 'vee-validate'
  import { useThemeColor } from '@/composables/useThemeColorV3'

  const props = defineProps({
    name: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: false
    },
    modelValue: {
      type: String,
      required: false
    },
    rows: {
      type: String,
      required: false,
      default: '3'
    },
    placeholder: {
      type: String,
      required: false,
      default: ''
    },
    rules: {
      type: Object as PropType<RuleExpression<any> | undefined>,
      required: false
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    heightFull: {
      type: Boolean,
      required: false,
      default: false
    },
    dataCy: {
      type: String,
      required: false,
      default: ''
    }
  })

  const { value, errorMessage, handleBlur, handleChange, meta, errors } = useField(
    props.name,
    props.rules,
    {
      syncVModel: true
    }
  )

  const themeColor = computed(() => useThemeColor().value)
</script>

<template>
  <div class="flex flex-col">
    <label
      v-if="label"
      class="mb-2 block text-sm font-medium"
    >
      {{ label }}
    </label>
    <div :class="[errorMessage ? 'relative' : '', heightFull ? 'h-full' : '']">
      <textarea
        v-model="value"
        class="block h-full w-full rounded-lg px-3 py-2 text-sm text-gray-800 placeholder:text-gray-500 focus:z-10"
        :name="props.name"
        :class="
          errorMessage
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
            : `border-gray-200 focus:border-${themeColor}-600 focus:ring-${themeColor}-600`
        "
        :rows="rows"
        :placeholder="placeholder"
        :disabled="props.disabled"
        :data-cy="dataCy"
        @input="handleChange"
        @change="handleChange"
        @blur="handleBlur"
      ></textarea>
      <div
        v-if="errorMessage"
        class="pointer-events-none absolute end-0 top-0 flex items-center p-3"
      >
        <ExclamationCircleIcon class="size-4 flex-shrink-0 text-red-500" />
      </div>
    </div>
    <p
      v-if="errorMessage"
      class="mt-1 text-xs text-red-600"
    >
      {{ errorMessage }}
    </p>
  </div>
</template>

<style scoped>
  textarea:disabled {
    background-color: #f9f9f9; /* Light grey background */
    color: #aaa; /* Gray text */
    cursor: not-allowed; /* Not allowed cursor */
  }
</style>
